import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';
import { c as cl } from './clsx.m.js';
import { c as createTypeValidator, a as createLogger, f as isUnset, u as updateElementStyle, d as isNumber, n as numToRem } from './utils.js';

// Constants
const component = 'cl-grid';
const gridConstants = {
  ALIGN_CONTENT: {
    FLEX_START: 'flex-start',
    FLEX_END: 'flex-end',
    STRETCH: 'stretch',
    CENTER: 'center',
    SPACE_BETWEEN: 'space-between',
    SPACE_AROUND: 'space-around',
    SPACE_EVENLY: 'space-evenly',
  },
  ALIGN_ITEM: {
    FLEX_START: 'flex-start',
    FLEX_END: 'flex-end',
    STRETCH: 'stretch',
    CENTER: 'center',
    BASELINE: 'baseline',
  },
  DIRECTION: {
    ROW: 'row',
    ROW_REVERSE: 'row-reverse',
    COLUMN: 'column',
    COLUMN_REVERSE: 'column-reverse',
  },
  ELEVATION: {
    0: 0,
    1: 1,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
  },
  JUSTIFY_CONTENT: {
    FLEX_START: 'flex-start',
    FLEX_END: 'flex-end',
    CENTER: 'center',
    SPACE_BETWEEN: 'space-between',
    SPACE_AROUND: 'space-around',
    SPACE_EVENLY: 'space-evenly',
  },
  SHAPE: {
    STATIC_SMALL: 'static-small',
    STATIC_LARGE: 'static-large',
    INTERACTIVE_SMALL: 'interactive-small',
    INTERACTIVE_LARGE: 'interactive-large',
    DYNAMIC_SMALL: 'dynamic-small',
    DYNAMIC_LARGE: 'dynamic-large',
    CIRCLE: 'circle',
  },
  SIZE: {
    1: 1,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 10,
    11: 11,
    12: 12,
    AUTO: 'auto',
  },
  SURFACE: {
    LIGHT: 'light',
    DARK: 'dark',
    MAIN: 'main',
    BASE: 'base',
  },
  WRAP: {
    NO_WRAP: 'nowrap',
    WRAP: 'wrap',
    WRAP_REVERSE: 'wrap-reverse',
  },
};
// Type-validators
const gridValidator = {
  alignContent: createTypeValidator(gridConstants.ALIGN_CONTENT),
  alignItems: createTypeValidator(gridConstants.ALIGN_ITEM),
  direction: createTypeValidator(gridConstants.DIRECTION),
  elevation: createTypeValidator(gridConstants.ELEVATION),
  justifyContent: createTypeValidator(gridConstants.JUSTIFY_CONTENT),
  shape: createTypeValidator(gridConstants.SHAPE),
  size: createTypeValidator(gridConstants.SIZE),
  surface: createTypeValidator(gridConstants.SURFACE),
  wrap: createTypeValidator(gridConstants.WRAP),
};

const clGridCss = ".sc-cl-grid-h{align-content:var(--custom-grid-align-content, var(--grid-align-content, flex-start));align-items:var(--custom-grid-align-items, var(--grid-align-items, flex-start));background-color:var(--custom-grid-surface-color, var(--grid-surface-color, rgb(var(--surface-color-main))));display:flex;justify-content:var(--custom-grid-justify-content, var(--grid-justify-content, flex-start));padding:var(--custom-grid-padding, var(--grid-padding, inherit))}.sc-cl-grid-h,.sc-cl-grid-h *.sc-cl-grid,.sc-cl-grid-h *.sc-cl-grid::before,.sc-cl-grid-h *.sc-cl-grid::after,.sc-cl-grid-h .sc-cl-grid-s>*,.sc-cl-grid-h .sc-cl-grid-s *,.sc-cl-grid-h .sc-cl-grid-s *::before,.sc-cl-grid-h .sc-cl-grid-s *::after{box-sizing:border-box}.grid--container.sc-cl-grid-h{border-radius:var(--custom-grid-shape, var(--grid-shape, var(--border-radius-static-small)));box-shadow:var(--custom-grid-elevation, var(--grid-elevation, 0));flex-direction:var(--custom-grid-direction, var(--grid-direction, row));flex-wrap:var(--custom-grid-wrap, var(--grid-wrap, wrap));padding:var(--custom-grid-padding, var(--grid-padding, inherit));width:100%}.grid--item.sc-cl-grid-h{--grid-item-width:var(--custom-grid-xs, 100%);flex:1 1 var(--grid-item-width);margin:0 0 0 calc(var(--custom-grid-offset, var(--grid-offset, 0)) / 12 * 100%);max-width:var(--grid-item-width);padding-bottom:calc(var(--custom-grid-row-gutter, var(--grid-row-gutter, inherit)) / 2);padding-left:calc(var(--custom-grid-column-gutter, var(--grid-column-gutter, inherit)) / 2);padding-right:calc(var(--custom-grid-column-gutter, var(--grid-column-gutter, inherit)) / 2);padding-top:calc(var(--custom-grid-row-gutter, var(--grid-row-gutter, inherit)) / 2)}@media screen and (min-width: 0){.grid--item.grid--xs.sc-cl-grid-h{--grid-item-width:var(--custom-grid-xs, 100%)}}@media screen and (min-width: 40rem){.grid--item.grid--sm.sc-cl-grid-h{--grid-item-width:var(--custom-grid-sm, var(--custom-grid-xs, 100%))}}@media screen and (min-width: 60rem){.grid--item.grid--md.sc-cl-grid-h{--grid-item-width:var(--custom-grid-md, var(--custom-grid-sm, var(--custom-grid-xs, 100%)))}}@media screen and (min-width: 80rem){.grid--item.grid--lg.sc-cl-grid-h{--grid-item-width:var(--custom-grid-lg, var(--custom-grid-md, var(--custom-grid-sm, var(--custom-grid-xs, 100%))))}}@media screen and (min-width: 120rem){.grid--item.grid--xl.sc-cl-grid-h{--grid-item-width:var(--custom-grid-xl, var(--custom-grid-lg, var(--custom-grid-md, var(--custom-grid-sm, var(--custom-grid-xs, 100%)))))}}";

const ClGrid = /*@__PURE__*/ proxyCustomElement(class ClGrid extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    /**
     * Function that handles applying the gutter size of the parent container, on the child grid items
     */
    this.updateChildGutter = (attributeName, gutterSize) => {
      const children = Array.from(this.element.children).filter(child => child.tagName === 'CL-GRID' && ![null, 'false'].includes(child.getAttribute('item')));
      children.forEach(child => {
        if (isUnset(gutterSize)) {
          child.removeAttribute(attributeName);
        }
        else {
          child.setAttribute(attributeName, `${gutterSize}`);
        }
      });
    };
    /**
     * Validates the given value for the given breakpoint and applies the styles.
     */
    this.updateBreakpoint = (breakpoint, value) => {
      const cssVariable = `--custom-grid-${breakpoint}`;
      const isAuto = value === gridConstants.SIZE.AUTO;
      switch (true) {
        case isUnset(value):
          updateElementStyle(this.element, undefined, cssVariable);
          break;
        case isUnset(value) || !gridValidator.size(isAuto ? value : +value):
          this.logger.error.attribute[breakpoint.toUpperCase()](`Given invalid value "${value}".`);
          break;
        default:
          updateElementStyle(this.element, isAuto ? value : `calc(${value} / 12 * 100%)`, cssVariable);
          break;
      }
    };
    this.alignContent = undefined;
    this.alignItems = undefined;
    this.columnGutter = undefined;
    this.container = false;
    this.direction = undefined;
    this.elevation = undefined;
    this.item = false;
    this.justifyContent = undefined;
    this.lg = undefined;
    this.md = undefined;
    this.offset = undefined;
    this.padding = undefined;
    this.rowGutter = undefined;
    this.shape = 'static-small';
    this.sm = undefined;
    this.surfaceColor = 'main';
    this.wrap = 'wrap';
    this.xl = undefined;
    this.xs = undefined;
  }
  /**
   * Validates the alignContent property and updates the styles accordingly
   */
  handleAlignContent(alignContent) {
    const cssVariable = '--custom-grid-align-content';
    switch (true) {
      case isUnset(alignContent):
        updateElementStyle(this.element, undefined, cssVariable);
        break;
      case !gridValidator.alignContent(alignContent):
        this.logger.error.attribute.alignContent(`Given invalid value "${alignContent}".`);
        break;
      default:
        updateElementStyle(this.element, alignContent, cssVariable);
        break;
    }
  }
  /**
   * Validates the alignItems property and updates the styles accordingly
   */
  handleAlignItems(alignItems) {
    const cssVariable = '--custom-grid-align-items';
    switch (true) {
      case isUnset(alignItems):
        updateElementStyle(this.element, undefined, cssVariable);
        break;
      case !gridValidator.alignItems(alignItems):
        this.logger.error.attribute.alignItems(`Given invalid value "${alignItems}".`);
        break;
      default:
        updateElementStyle(this.element, alignItems, cssVariable);
        break;
    }
  }
  /**
   * Validates the columnGutter property, updates the styles accordingly and applies this value to the nested grid items
   */
  handleColumnGutter(columnGutter) {
    const cssVariable = '--custom-grid-column-gutter';
    switch (true) {
      case isUnset(columnGutter):
        updateElementStyle(this.element, undefined, cssVariable);
        break;
      case !isNumber(columnGutter):
        this.logger.error.attribute.columnGutter(`Given invalid value "${columnGutter}".`);
        break;
      default:
        updateElementStyle(this.element, numToRem(columnGutter), cssVariable);
        break;
    }
    if (this.container)
      this.updateChildGutter('column-gutter', columnGutter);
  }
  /**
   * Validates the direction property and updates the styles accordingly
   */
  handleGridDirection(direction) {
    const cssVariable = '--custom-grid-direction';
    switch (true) {
      case isUnset(direction):
        updateElementStyle(this.element, undefined, cssVariable);
        break;
      case !gridValidator.direction(direction):
        this.logger.error.attribute.direction(`Given invalid value "${direction}".`);
        break;
      default:
        updateElementStyle(this.element, direction, cssVariable);
        break;
    }
  }
  /**
   * Validates the elevation property and updates the styles accordingly
   */
  handleElevation(elevation) {
    const cssVariable = '--custom-grid-elevation';
    if (isUnset(elevation)) {
      updateElementStyle(this.element, undefined, cssVariable);
    }
    else if (isNumber(elevation)) {
      if (!gridValidator.elevation(+elevation)) {
        this.logger.error.attribute.elevation(`Given invalid value "${elevation}".`);
      }
      else {
        updateElementStyle(this.element, `var(--elevation-${elevation})`, cssVariable);
      }
    }
  }
  /**
   * Validates the justifyContent property and updates the styles accordingly
   */
  handleJustifyContent(justifyContent) {
    const cssVariable = '--custom-grid-justify-content';
    switch (true) {
      case isUnset(justifyContent):
        updateElementStyle(this.element, undefined, cssVariable);
        break;
      case !gridValidator.justifyContent(justifyContent):
        this.logger.error.attribute.justifyContent(`Given invalid value "${justifyContent}".`);
        break;
      default:
        updateElementStyle(this.element, justifyContent, cssVariable);
        break;
    }
  }
  /**
   * Validates the lg property and updates the styles accordingly
   */
  handleLg(lg) {
    this.updateBreakpoint('lg', lg);
  }
  /**
   * Validates the md property and updates the styles accordingly
   */
  handleMd(md) {
    this.updateBreakpoint('md', md);
  }
  /**
   * Validates the offset property and updates the styles accordingly
   */
  handleOffset(offset) {
    const cssVariable = '--custom-grid-offset';
    switch (true) {
      case isUnset(offset):
        updateElementStyle(this.element, undefined, cssVariable);
        break;
      case isUnset(offset) || !gridValidator.size(+offset):
        this.logger.error.attribute.offset(`Given invalid value "${offset}".`);
        break;
      default:
        updateElementStyle(this.element, `${offset}`, cssVariable);
        break;
    }
  }
  /**
   * Validates the padding property and updates the styles accourdingly
   */
  handlePadding(padding) {
    const cssVariable = '--custom-grid-padding';
    switch (true) {
      case isUnset(padding):
        updateElementStyle(this.element, undefined, cssVariable);
        break;
      case !!/\s/g.exec(padding || ''): {
        const splitPadding = padding.split(' ');
        updateElementStyle(this.element, splitPadding.map(value => (isNumber(value) ? numToRem(+value) : value)).join(' '), cssVariable);
        break;
      }
      case isNumber(padding):
        updateElementStyle(this.element, numToRem(padding), cssVariable);
        break;
      default:
        this.logger.error.attribute.padding(`Given invalid value "${padding}".`);
        break;
    }
  }
  /**
   * Validates the rowGutter property, updates the styles accordingly and applies this value to the nested grid items
   */
  handleRowGutter(rowGutter) {
    const cssVariable = '--custom-grid-row-gutter';
    switch (true) {
      case isUnset(rowGutter):
        updateElementStyle(this.element, undefined, cssVariable);
        break;
      case !isNumber(rowGutter):
        this.logger.error.attribute.rowGutter(`Given invalid value "${rowGutter}".`);
        break;
      default:
        updateElementStyle(this.element, numToRem(rowGutter), cssVariable);
        break;
    }
    if (this.container)
      this.updateChildGutter('row-gutter', rowGutter);
  }
  /**
   * Validates the shape property and updates the styles accordingly
   */
  handleShape(shape) {
    const cssVariable = '--custom-grid-shape';
    switch (true) {
      case isUnset(shape):
        updateElementStyle(this.element, undefined, cssVariable);
        break;
      case !gridValidator.shape(shape):
        this.logger.error.attribute.shape(`Given invalid value "${shape}".`);
        break;
      default:
        updateElementStyle(this.element, `var(--border-radius-${shape})`, cssVariable);
        break;
    }
  }
  /**
   * Validates the sm property and updates the styles accordingly
   */
  handleSm(sm) {
    this.updateBreakpoint('sm', sm);
  }
  /**
   * Validates the surfaceColor property and updates the styles accordingly
   */
  handleSurface(surfaceColor) {
    const cssVariable = '--custom-grid-surface-color';
    switch (true) {
      case isUnset(surfaceColor):
        updateElementStyle(this.element, undefined, cssVariable);
        break;
      case !gridValidator.surface(surfaceColor):
        this.logger.error.attribute.surfaceColor(`Given invalid value "${surfaceColor}".`);
        break;
      default:
        updateElementStyle(this.element, `rgb(var(--surface-color-${surfaceColor}))`, cssVariable);
        break;
    }
  }
  /**
   * Validates the wrap property and updates the styles accordingly
   */
  handleWrap(wrap) {
    const cssVariable = '--custom-grid-wrap';
    switch (true) {
      case isUnset(wrap):
        updateElementStyle(this.element, undefined, cssVariable);
        break;
      case !gridValidator.wrap(wrap):
        this.logger.error.attribute.wrap(`Given invalid value "${wrap}".`);
        break;
      default:
        updateElementStyle(this.element, wrap, cssVariable);
        break;
    }
  }
  /**
   * Validates the xl property and updates the styles accordingly
   */
  handleXl(xl) {
    this.updateBreakpoint('xl', xl);
  }
  /**
   * Validates the xs property and updates the styles accordingly
   */
  handleXs(xs) {
    this.updateBreakpoint('xs', xs);
  }
  /**
   * When the component first connects to the DOM, check for the values
   */
  connectedCallback() {
    this.handleAlignContent(this.alignContent);
    this.handleAlignItems(this.alignItems);
    this.handleColumnGutter(this.columnGutter);
    this.handleElevation(this.elevation);
    this.handleGridDirection(this.direction);
    this.handleJustifyContent(this.justifyContent);
    this.handleLg(this.lg);
    this.handleMd(this.md);
    this.handleOffset(this.offset);
    this.handlePadding(this.padding);
    this.handleRowGutter(this.rowGutter);
    this.handleShape(this.shape);
    this.handleSm(this.sm);
    this.handleSurface(this.surfaceColor);
    this.handleWrap(this.wrap);
    this.handleXl(this.xl);
    this.handleXs(this.xs);
  }
  render() {
    const { container, item, lg, md, sm, xl, xs } = this;
    return (h(Host, { class: cl('grid', {
        'grid--item': !!item,
        'grid--container': !!container,
        'grid--xs': !!xs,
        'grid--sm': !!sm,
        'grid--md': !!md,
        'grid--lg': !!lg,
        'grid--xl': !!xl,
      }) }, h("slot", null)));
  }
  get element() { return this; }
  static get watchers() { return {
    "alignContent": ["handleAlignContent"],
    "alignItems": ["handleAlignItems"],
    "columnGutter": ["handleColumnGutter"],
    "direction": ["handleGridDirection"],
    "elevation": ["handleElevation"],
    "justifyContent": ["handleJustifyContent"],
    "lg": ["handleLg"],
    "md": ["handleMd"],
    "offset": ["handleOffset"],
    "padding": ["handlePadding"],
    "rowGutter": ["handleRowGutter"],
    "shape": ["handleShape"],
    "sm": ["handleSm"],
    "surfaceColor": ["handleSurface"],
    "wrap": ["handleWrap"],
    "xl": ["handleXl"],
    "xs": ["handleXs"]
  }; }
  static get style() { return clGridCss; }
}, [6, "cl-grid", {
    "alignContent": [513, "align-content"],
    "alignItems": [513, "align-items"],
    "columnGutter": [514, "column-gutter"],
    "container": [516],
    "direction": [1],
    "elevation": [514],
    "item": [516],
    "justifyContent": [513, "justify-content"],
    "lg": [520],
    "md": [520],
    "offset": [520],
    "padding": [520],
    "rowGutter": [514, "row-gutter"],
    "shape": [1],
    "sm": [520],
    "surfaceColor": [1, "surface-color"],
    "wrap": [1],
    "xl": [520],
    "xs": [520]
  }]);
function defineCustomElement() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-grid"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-grid":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClGrid);
      }
      break;
  } });
}

export { ClGrid as C, defineCustomElement as d };

//# sourceMappingURL=cl-grid2.js.map